
import { useState, useEffect, useCallback } from "react";
import { socialApi } from "@/api/social";

/**
 * Hook for handling favorites functionality
 */
export const useSocialFavorites = (
  derivativeId: string,
  isAuthenticated: boolean,
  updateCount: (type: "favorites_count", increment: boolean) => void
) => {
  const [isFavorited, setIsFavorited] = useState(false);
  
  // Check if the derivative is already favorited
  useEffect(() => {
    const checkIfFavorited = async () => {
      if (!derivativeId || !isAuthenticated) return;
      
      try {
        const favorited = await socialApi.checkIfFavorited(derivativeId);
        setIsFavorited(favorited);
      } catch (error) {
        console.error(`Error checking if derivative ${derivativeId} is favorited:`, error);
      }
    };
    
    checkIfFavorited();
  }, [derivativeId, isAuthenticated]);

  // Favorite/Unfavorite function
  const toggleFavorite = useCallback(async () => {
    if (!isAuthenticated) {
      return { requireAuth: true, action: "favorite" };
    }
    
    const prevIsFavorited = isFavorited;
    
    // Optimistic UI update
    setIsFavorited(!prevIsFavorited);
    updateCount("favorites_count", !prevIsFavorited);
    
    try {
      const result = await socialApi.favoriteDerivative(derivativeId);
      
      if (!result.success) {
        // Revert on failure
        setIsFavorited(prevIsFavorited);
        updateCount("favorites_count", prevIsFavorited);
      }
      
      return { requireAuth: false, success: result.success };
    } catch (error) {
      // Revert on error
      setIsFavorited(prevIsFavorited);
      updateCount("favorites_count", prevIsFavorited);
      console.error("Error toggling favorite:", error);
      return { requireAuth: false, success: false };
    }
  }, [derivativeId, isAuthenticated, isFavorited, updateCount]);

  return {
    isFavorited,
    toggleFavorite
  };
};
